import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, Flex, Grid, Heading, HStack, Text, Tooltip} from "@chakra-ui/react";
import {ChevronLeftIcon, ChevronRightIcon} from "@chakra-ui/icons";
import {FiPlus} from "react-icons/fi";
import {BsSoundwave} from "react-icons/bs";
import {useMutation, useQuery} from "@apollo/client";
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import {
    AreaLanguageOption,
    AreaZoneTemplate,
    RulesLogic,
    SoundFile,
    Spot,
    TextTranslation,
    Zone
} from "../../components/types";
import {v4 as uuidv4} from "uuid";
import {registerSoundFileMutation} from "../../components/soundfiles/soundFileQueries";
import {ExistingSoundOption} from "../../components/soundfiles/SoundFileLibraryDialog";
import {
    getSoundLibraryDragPayload,
    hasSoundLibraryDragType
} from "../../components/soundfiles/SoundFileDrawer";
import {moveSpotToPositionMutation} from "../../components/spots/graphQl";
import {UserContext, UserContextI} from "../../UserContext";
import {useConfirmDialog} from "../../components/common/ConfirmDialogProvider";
import {useUploadQueueContext} from "../../components/uploadQueue/UploadQueueContext";
import {
    buildTriggerChain,
    createCueSpot,
    formatMillisecondsAsTimeCode,
    getNextCueIndex,
    spotsToOrderedCues
} from "./cueListHelpers";
import CueListRow from "./CueListRow";
import CueListInspector from "./CueListInspector";
import CueListAmbientRow from "./CueListAmbientRow";
import {deriveCueGroups, UNGROUPED_CUE_GROUP_ID} from "./cueGroupHelpers";
import CueTriggerEditorDialog from "./CueTriggerEditorDialog";
import CueMultiSpotTriggerEditorDialog, {SpotTriggerContext} from "./CueMultiSpotTriggerEditorDialog";
import {useTranslationDialog} from "../../components/translations/TranslationDialogProvider";
import {getTextTranslationsQuery} from "../../components/translations/textTranslationQueries";
import {
    buildSpotTranslationRowsByClientId,
    getSpotTranslationState as computeSpotTranslationState,
    LocalizedContentState,
    normalizeLanguageCode,
    SPOT_TRANSLATION_FIELDS,
} from "../../components/translations/spotTranslationState";
import {
    applyCueTriggerSetToSpot,
    CueTriggerSet,
    extractCueTriggerSet,
    hasCueTriggers,
    toLegacySharedTriggerConfig
} from "./cueTriggerHelpers";
import {CUE_LIST_GRID_TEMPLATE} from "./cueListLayout";
import {
    buildAmbientZonesFromEntries,
    buildMergedCueListRules,
    compareVersions,
    CueListEntry,
    CueListGroupSection,
    getAmbientAnchorRule,
    getAmbientRowKey,
    getSpotRowKey,
    hasEntryRules,
    isManagedCueRule,
    mergeRulesWithoutDuplicates,
} from "./cueListRuleBuilders";
import {
    AMBIENT_ROW_DRAG_TYPE,
    computeReorderMarkerPosition,
    confirmSoundReplacement,
    CUE_ROW_DRAG_TYPE,
    getAmbientRowDragId,
    getCueRowDragId,
    hasDroppedFiles,
    hasExistingSound,
} from "./cueListDragHandlers";
import {useCueListSoundAssignment} from "./useCueListSoundAssignment";

type Props = {
    isSoundDrawerOpen: boolean;
    onToggleSoundDrawer: () => void;
    existingSoundOptions: ExistingSoundOption[];
    isLibraryLoading: boolean;
    onRequestLibrary: () => void;
};

const CueListPanel = ({isSoundDrawerOpen, onToggleSoundDrawer, existingSoundOptions, isLibraryLoading, onRequestLibrary}: Props) => {
    const appContext = useContext<AppContextI>(AppContext);
    const userContext = useContext<UserContextI>(UserContext);
    const {confirm} = useConfirmDialog();
    const {open: openTranslationDialog} = useTranslationDialog();
    const area = appContext.getArea();
    const defaultRunOnes = !appContext.isOnDemand();
    const allowBeaconTriggers = appContext.isBleEnabled();
    const allSpots = appContext.getSpots();
    const canEdit = appContext.isDraft();
    const splitContainerRef = useRef<HTMLDivElement | null>(null);
    const [inspectorHeightPx, setInspectorHeightPx] = useState<number>(420);
    const [isSplitterDragging, setIsSplitterDragging] = useState<boolean>(false);
    const [originalSplitterUserSelect, setOriginalSplitterUserSelect] = useState<string | undefined>(undefined);
    const splitterDividerPx = 6;
    const splitterMinInspectorPx = 200;
    const splitterMinTopPx = 80;
    const [moveSpotToPosition, {loading: isReordering}] = useMutation(moveSpotToPositionMutation);
    const [registerSoundFile] = useMutation(registerSoundFileMutation);
    const [selectedCueIds, setSelectedCueIds] = useState<string[]>([]);
    const [lastSelectedCueId, setLastSelectedCueId] = useState<string | undefined>(undefined);
    const [dropTargetCueId, setDropTargetCueId] = useState<string | undefined>(undefined);
    const [dropTargetAmbientZoneId, setDropTargetAmbientZoneId] = useState<string | undefined>(undefined);
    const [draggingCueId, setDraggingCueId] = useState<string | undefined>(undefined);
    const [draggingAmbientZoneId, setDraggingAmbientZoneId] = useState<string | undefined>(undefined);
    const [reorderTargetRowKey, setReorderTargetRowKey] = useState<string | undefined>(undefined);
    const [reorderMarkerPosition, setReorderMarkerPosition] = useState<"top" | "bottom">("top");
    const [selectedAmbientZoneId, setSelectedAmbientZoneId] = useState<string | undefined>(undefined);
    const [isListDropTarget, setIsListDropTarget] = useState<boolean>(false);
    const [spotTranslations, setSpotTranslations] = useState<TextTranslation[]>([]);
    const [isDefaultTriggerDialogOpen, setDefaultTriggerDialogOpen] = useState<boolean>(false);
    const [triggerEditorSpotIds, setTriggerEditorSpotIds] = useState<string[]>([]);
    const [triggerEditorAmbientZoneId, setTriggerEditorAmbientZoneId] = useState<string | undefined>(undefined);
    const {enqueueUploads} = useUploadQueueContext();

    const orderedCues = useMemo(() => spotsToOrderedCues(allSpots), [allSpots]);
    const areaLanguages = useMemo(() => area?.languages?.available || [], [area?.languages?.available]);
    const areaLanguageCodes = useMemo(
        () => areaLanguages
            .map((language: AreaLanguageOption) => normalizeLanguageCode(language.code))
            .filter((languageCode, index, values) => !!languageCode && values.indexOf(languageCode) === index),
        [areaLanguages]
    );
    const normalizedDefaultLanguage = normalizeLanguageCode(area?.languages?.default);
    const translatableLanguageCodes = useMemo(
        () => areaLanguageCodes.filter((languageCode) => languageCode !== normalizedDefaultLanguage),
        [areaLanguageCodes, normalizedDefaultLanguage]
    );
    const cueClientIds = useMemo(
        () => orderedCues.map((spot) => spot.key).filter((key, index, values) => !!key && values.indexOf(key) === index),
        [orderedCues]
    );
    const {data: cueTranslationsData} = useQuery(getTextTranslationsQuery, {
        variables: {
            areaId: area?.id,
            clientIds: cueClientIds,
            languageCodes: areaLanguageCodes,
            translationKeys: SPOT_TRANSLATION_FIELDS.map((field) => field.translationKey),
            publishedAt: appContext.getDisplayedVersion()?.publishedAt || 0
        },
        skip: !area?.id || areaLanguageCodes.length === 0 || cueClientIds.length === 0,
        fetchPolicy: "network-only"
    });

    useEffect(() => {
        setSpotTranslations(cueTranslationsData?.getTextTranslations || []);
    }, [cueTranslationsData]);

    useEffect(() => {
        if (!isSplitterDragging) return;
        const onMouseMove = (event: MouseEvent) => {
            if (!splitContainerRef.current) return;
            const rect = splitContainerRef.current.getBoundingClientRect();
            const usableHeight = rect.height - splitterDividerPx;
            const offsetY = event.clientY - rect.top;
            const proposedBottom = usableHeight - offsetY;
            const maxBottom = Math.max(splitterMinInspectorPx, usableHeight - splitterMinTopPx);
            const clamped = Math.max(splitterMinInspectorPx, Math.min(maxBottom, proposedBottom));
            setInspectorHeightPx(clamped);
        };
        const onMouseUp = () => {
            setIsSplitterDragging(false);
            if (originalSplitterUserSelect !== undefined) {
                document.body.style.userSelect = originalSplitterUserSelect;
                setOriginalSplitterUserSelect(undefined);
            }
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [isSplitterDragging, originalSplitterUserSelect]);

    // Build a merged list of audio cues (spots) and ambient cues (zones) for display.
    // Ambient zones can be anchored after a spot (SPOT_FINISHED / SPOT_TRIGGERED),
    // before a spot (SPOT_NOT_TRIGGERED / SPOT_NOT_FINISHED when mixed with other rules),
    // or at the top when they only have NOT-spot rules.
    const mergedCueList = useMemo<CueListEntry[]>(() => {
        const ambientZones: Zone[] = area?.zones?.ambientZones || [];
        // Map spotKey → ambient zones anchored relative to that spot.
        const ambientAfterSpot = new Map<string, Zone[]>();
        const ambientBeforeSpot = new Map<string, Zone[]>();
        const topAnchoredAmbients: Zone[] = [];
        const validSpotKeys = new Set(orderedCues.map((spot) => spot.key).filter((key): key is string => !!key));
        const unlinkedAmbients: Zone[] = [];

        ambientZones.forEach(zone => {
            const anchor = getAmbientAnchorRule(zone);
            if (anchor?.position === "top") {
                topAnchoredAmbients.push(zone);
                return;
            }
            if (anchor?.spotKey && validSpotKeys.has(anchor.spotKey)) {
                if (anchor.position === "before") {
                    const beforeList = ambientBeforeSpot.get(anchor.spotKey) || [];
                    beforeList.push(zone);
                    ambientBeforeSpot.set(anchor.spotKey, beforeList);
                    return;
                }
                const afterList = ambientAfterSpot.get(anchor.spotKey) || [];
                afterList.push(zone);
                ambientAfterSpot.set(anchor.spotKey, afterList);
            } else {
                unlinkedAmbients.push(zone);
            }
        });

        const entries: CueListEntry[] = [];
        // Unlinked ambient zones (no spot sequencing rule) are unconditional and should run from the top.
        topAnchoredAmbients.forEach(zone => entries.push({kind: "ambient", zone}));
        unlinkedAmbients.forEach(zone => entries.push({kind: "ambient", zone}));
        orderedCues.forEach((spot, index) => {
            const linkedBefore = spot.key ? ambientBeforeSpot.get(spot.key) : undefined;
            if (linkedBefore) {
                linkedBefore.forEach(zone => entries.push({kind: "ambient", zone}));
            }
            entries.push({kind: "spot", spot, index});
            const linkedAfter = spot.key ? ambientAfterSpot.get(spot.key) : undefined;
            if (linkedAfter) {
                linkedAfter.forEach(zone => entries.push({kind: "ambient", zone}));
            }
        });

        return entries;
    }, [orderedCues, area?.zones?.ambientZones]);
    const spotTranslationRowsByClientId = useMemo(
        () => buildSpotTranslationRowsByClientId(spotTranslations),
        [spotTranslations]
    );

    const orgAppVersion = appContext.getOrganizationAppVersion() || "3.15";
    const useAmbientRules = compareVersions(orgAppVersion, "4.1") >= 0;

    const {groupedCueSections, topUngroupedEntries} = useMemo(() => {
        const ambientZones: Zone[] = area?.zones?.ambientZones || [];
        const derivedGroups = deriveCueGroups(orderedCues, ambientZones);
        const spotGroupIdByKey = new Map<string, string>();
        const ambientGroupIdById = new Map<string, string>();
        const sections = new Map<string, CueListGroupSection>();
        const topUngrouped: CueListEntry[] = [];

        derivedGroups.forEach((group) => {
            sections.set(group.id, {
                entries: []
            });
            group.spotKeys.forEach((spotKey) => spotGroupIdByKey.set(spotKey, group.id));
            group.ambientZoneIds.forEach((zoneId) => ambientGroupIdById.set(zoneId, group.id));
        });

        const ensureFloatingSection = () => {
            const existing = sections.get(UNGROUPED_CUE_GROUP_ID);
            if (existing) {
                return existing;
            }
            const floating: CueListGroupSection = {
                entries: []
            };
            sections.set(UNGROUPED_CUE_GROUP_ID, floating);
            return floating;
        };

        if (useAmbientRules) {
            // In ambient rules mode, all entries form one continuous sequence.
            const singleSection: CueListGroupSection = {entries: [...mergedCueList]};
            return {
                groupedCueSections: singleSection.entries.length > 0 ? [singleSection] : [],
                topUngroupedEntries: topUngrouped
            };
        }

        mergedCueList.forEach((entry) => {
            const groupId = entry.kind === "spot"
                ? (entry.spot.key ? spotGroupIdByKey.get(entry.spot.key) : undefined)
                : ambientGroupIdById.get(entry.zone.id);
            const hasRules = hasEntryRules(entry);
            const belongsToConnectedGroup = !!groupId
                && groupId !== UNGROUPED_CUE_GROUP_ID
                && sections.has(groupId);

            if (!hasRules && !belongsToConnectedGroup) {
                topUngrouped.push(entry);
                return;
            }
            if (groupId && sections.has(groupId)) {
                sections.get(groupId)?.entries.push(entry);
                return;
            }
            ensureFloatingSection().entries.push(entry);
        });

        return {
            groupedCueSections: Array.from(sections.values()).filter((section) => section.entries.length > 0),
            topUngroupedEntries: topUngrouped
        };
    }, [area?.zones?.ambientZones, mergedCueList, orderedCues, useAmbientRules]);
    const visualCueSections = useMemo<CueListEntry[][]>(() => {
        const sections: CueListEntry[][] = [];
        if (topUngroupedEntries.length > 0) {
            sections.push(topUngroupedEntries);
        }
        groupedCueSections.forEach((section) => {
            if (section.entries.length > 0) {
                sections.push(section.entries);
            }
        });
        return sections;
    }, [groupedCueSections, topUngroupedEntries]);

    const buildSpotTranslationFields = useCallback((spot: Spot) => {
        return SPOT_TRANSLATION_FIELDS.map((field) => {
            if (field.translationKey === "spot.name") {
                return {
                    ...field,
                    fallbackValue: spot.name
                };
            }
            if (field.translationKey === "spot.summary") {
                return {
                    ...field,
                    fallbackValue: spot.summary
                };
            }
            return {
                ...field,
                fallbackValue: spot.description
            };
        });
    }, []);

    const getSpotTranslationState = useCallback(
        (spot: Spot) => computeSpotTranslationState(spot, spotTranslationRowsByClientId, translatableLanguageCodes),
        [spotTranslationRowsByClientId, translatableLanguageCodes]
    );

    const getLocalizedStatusColor = useCallback((state: LocalizedContentState, isSelected: boolean) => {
        if (isSelected) {
            if (state === "fullyTranslated") {
                return "#15803d";
            }
            if (state === "partiallyTranslated") {
                return "#d97706";
            }
            return "#a0aec0";
        }

        if (state === "fullyTranslated") {
            return "#16a34a";
        }
        if (state === "partiallyTranslated") {
            return "#dd6b20";
        }
        return "#a0aec0";
    }, []);

    const getLocalizedStatusLabel = useCallback((state: LocalizedContentState) => {
        if (state === "fullyTranslated") {
            return "Fully translated";
        }
        if (state === "partiallyTranslated") {
            return "Partially translated";
        }
        return "Not translated";
    }, []);

    const mergeSpotTranslation = useCallback((savedTranslation: TextTranslation) => {
        setSpotTranslations((previous) => {
            const filtered = previous.filter((entry) => entry.id !== savedTranslation.id &&
                !(entry.clientId === savedTranslation.clientId &&
                    normalizeLanguageCode(entry.languageCode) === normalizeLanguageCode(savedTranslation.languageCode) &&
                    entry.translationKey === savedTranslation.translationKey));
            return [...filtered, savedTranslation];
        });
    }, []);

    const getCueListEntryKey = useCallback((entry: CueListEntry): string => {
        if (entry.kind === "spot") {
            return getSpotRowKey(entry.spot.id);
        }
        return getAmbientRowKey(entry.zone.id);
    }, []);

    const mergedEntryIndexByKey = useMemo(() => {
        const indexByKey = new Map<string, number>();
        mergedCueList.forEach((entry, index) => {
            indexByKey.set(getCueListEntryKey(entry), index);
        });
        return indexByKey;
    }, [getCueListEntryKey, mergedCueList]);

    const normalizeAmbientCueRules = useCallback((entries: CueListEntry[], zones?: Zone[]) => {
        const ambientZones = zones || area?.zones?.ambientZones || [];
        if (ambientZones.length === 0) {
            return ambientZones;
        }
        if (!useAmbientRules) {
            return buildAmbientZonesFromEntries(entries, ambientZones);
        }
        const {updatedZones, spotManagedRules} = buildMergedCueListRules(entries, ambientZones);
        // Apply managed rules to spots
        spotManagedRules.forEach((managedRules, spotId) => {
            const spot = appContext.getSpot(spotId);
            if (!spot) return;
            const preservedRules = (spot.triggerRules?.rules || []).filter((r) => !isManagedCueRule(r));
            const nextRules = mergeRulesWithoutDuplicates(preservedRules, managedRules);
            appContext.updateSpot(spot, {
                ...spot,
                triggerRules: nextRules.length > 0 ? {
                    logic: spot.triggerRules?.logic || RulesLogic.AND,
                    rules: nextRules
                } : spot.triggerRules
            });
        });
        return updatedZones;
    }, [appContext, area?.zones?.ambientZones, useAmbientRules]);

    const defaultTriggerSet = useMemo<CueTriggerSet>(
        () => extractCueTriggerSet(area?.defaults, orderedCues[0]),
        [area?.defaults, orderedCues]
    );

    useEffect(() => {
        if (orderedCues.length === 0) {
            setSelectedCueIds([]);
            setLastSelectedCueId(undefined);
            return;
        }
        setSelectedCueIds((previous) => {
            const filtered = previous.filter((id) => orderedCues.some((cue) => cue.id === id));
            return filtered.length > 0 ? filtered : [orderedCues[0].id];
        });
    }, [orderedCues]);

    useEffect(() => {
        if (selectedCueIds.length === 0) {
            setLastSelectedCueId(undefined);
            return;
        }
        if (lastSelectedCueId && selectedCueIds.includes(lastSelectedCueId)) {
            return;
        }
        setLastSelectedCueId(selectedCueIds[selectedCueIds.length - 1]);
    }, [lastSelectedCueId, selectedCueIds]);

    useEffect(() => {
        if (!selectedAmbientZoneId) {
            return;
        }
        const ambientZones = area?.zones?.ambientZones || [];
        if (!ambientZones.some((zone) => zone.id === selectedAmbientZoneId)) {
            setSelectedAmbientZoneId(undefined);
        }
    }, [area?.zones?.ambientZones, selectedAmbientZoneId]);

    useEffect(() => {
        if (!isSoundDrawerOpen) {
            return;
        }
        onRequestLibrary();
    }, [isSoundDrawerOpen, onRequestLibrary]);

    const handleDefaultTriggerSave = useCallback((nextTriggerSet: CueTriggerSet) => {
        if (!canEdit || !area) {
            return;
        }
        const legacySharedTrigger = toLegacySharedTriggerConfig(nextTriggerSet);
        const nextArea = {
            ...area,
            defaults: {
                ...(area.defaults || {}),
                beaconTriggers: nextTriggerSet.beaconTriggers,
                wiFiTriggers: nextTriggerSet.wiFiTriggers,
                sharedTrigger: legacySharedTrigger
            }
        };
        appContext.updateArea(area, nextArea);
        if (orderedCues.length === 0) {
            return;
        }
        const firstSpot = orderedCues[0];
        const triggerFields = applyCueTriggerSetToSpot(nextTriggerSet);
        appContext.updateSpot(firstSpot, {...firstSpot, ...triggerFields});
    }, [appContext, area, canEdit, orderedCues]);

    const applyFullChain = useCallback((cues: Spot[]) => {
        if (cues.length === 0) return;
        const chainUpdates = buildTriggerChain(cues);
        const triggerFields = applyCueTriggerSetToSpot(defaultTriggerSet);
        chainUpdates.forEach(({spot, updates: partial}, idx) => {
            const merged = {...spot, ...partial};
            if (idx === 0) {
                Object.assign(merged, triggerFields);
            }
            appContext.updateSpot(spot, merged);
        });

        if (useAmbientRules) {
            // Also rebuild ambient rules since spot ordering changed
            const ambientZones = area?.zones?.ambientZones || [];
            if (ambientZones.length > 0) {
                // Build projected merged list: ambients stay in place, spots fill spot slots in new order
                const cueIdSet = new Set(cues.map((s) => s.id));
                const spotPositions: number[] = [];
                const projectedEntries: CueListEntry[] = [];
                mergedCueList.forEach((entry) => {
                    if (entry.kind === "ambient") {
                        projectedEntries.push(entry);
                    } else if (cueIdSet.has(entry.spot.id)) {
                        spotPositions.push(projectedEntries.length);
                        projectedEntries.push(entry); // placeholder
                    }
                    // Skip removed spots
                });
                cues.forEach((spot, i) => {
                    if (i < spotPositions.length) {
                        projectedEntries[spotPositions[i]] = {kind: "spot", spot, index: i};
                    }
                });
                const {updatedZones, spotManagedRules} = buildMergedCueListRules(projectedEntries, ambientZones);
                // Apply managed rules to spots
                spotManagedRules.forEach((managedRules, spotId) => {
                    const spot = cues.find((s) => s.id === spotId);
                    if (!spot) return;
                    const latestSpot = appContext.getSpot(spotId) || spot;
                    const preservedRules = (latestSpot.triggerRules?.rules || []).filter((r) => !isManagedCueRule(r));
                    const nextRules = mergeRulesWithoutDuplicates(preservedRules, managedRules);
                    appContext.updateSpot(latestSpot, {
                        ...latestSpot,
                        triggerRules: nextRules.length > 0 ? {
                            logic: latestSpot.triggerRules?.logic || RulesLogic.AND,
                            rules: nextRules
                        } : latestSpot.triggerRules
                    });
                });
                // Update ambient zones
                const currentArea = appContext.getArea();
                if (currentArea) {
                    appContext.setArea({
                        ...currentArea,
                        zones: {
                            ...currentArea.zones,
                            ambientZones: updatedZones
                        }
                    });
                }
            }
        }
    }, [appContext, area?.zones?.ambientZones, defaultTriggerSet, mergedCueList, useAmbientRules]);

    const applyFullChainByIds = useCallback((spotIds: string[], fallbackSpots: Spot[] = []) => {
        if (spotIds.length === 0) {
            return;
        }
        const latestSpots = appContext.getSpots() || [];
        const latestById = new Map<string, Spot>();
        latestSpots.forEach((spot) => latestById.set(spot.id, spot));
        fallbackSpots.forEach((spot) => {
            if (!latestById.has(spot.id)) {
                latestById.set(spot.id, spot);
            }
        });
        const ordered = spotIds
            .map((id) => latestById.get(id))
            .filter((spot): spot is Spot => !!spot);
        if (ordered.length > 0) {
            applyFullChain(ordered);
        }
    }, [appContext, applyFullChain]);

    const mergeSpotsIntoContext = useCallback((spotsToMerge: Spot[]) => {
        if (spotsToMerge.length === 0) {
            return;
        }
        const currentSpots = appContext.getSpots() || [];
        const byId = new Map<string, Spot>();
        currentSpots.forEach((spot) => byId.set(spot.id, spot));
        spotsToMerge.forEach((spot) => byId.set(spot.id, spot));
        appContext.setSpots(Array.from(byId.values()));
    }, [appContext]);

    const buildOptimisticCreatedCues = useCallback((existingCues: Spot[], createdCues: Spot[]) => {
        if (createdCues.length === 0) {
            return createdCues;
        }
        const intendedCues = [...existingCues, ...createdCues];
        const triggerFields = applyCueTriggerSetToSpot(defaultTriggerSet);
        const chainUpdates = buildTriggerChain(intendedCues);
        const mergedById = new Map<string, Spot>();
        chainUpdates.forEach(({spot, updates}, index) => {
            const merged: Spot = {
                ...spot,
                ...updates
            };
            if (index === 0) {
                merged.beaconTriggers = triggerFields.beaconTriggers || [];
                merged.wiFiTriggers = triggerFields.wiFiTriggers || [];
            }
            mergedById.set(spot.id, merged);
        });
        return createdCues.map((spot) => mergedById.get(spot.id) || spot);
    }, [defaultTriggerSet]);

    const handleAddCue = useCallback(() => {
        if (!area?.id || !canEdit) return;
        const nextIndex = getNextCueIndex(orderedCues);
        const cueSpot = createCueSpot(area.id, `Cue ${nextIndex + 1}`, nextIndex, defaultRunOnes);

        appContext.createSpot(cueSpot as Spot, {select: false, suppressLocalInsert: true})?.then((createdSpot) => {
            if (!createdSpot) return;
            const createdSpotWithDefaults = hasCueTriggers(defaultTriggerSet)
                ? {
                    ...createdSpot,
                    ...applyCueTriggerSetToSpot(defaultTriggerSet)
                }
                : createdSpot;
            const optimisticCreated = buildOptimisticCreatedCues(orderedCues, [createdSpotWithDefaults]);
            mergeSpotsIntoContext(optimisticCreated);
            // Rebuild full chain including the new cue
            applyFullChainByIds(
                [...orderedCues.map((spot) => spot.id), ...optimisticCreated.map((spot) => spot.id)],
                [...orderedCues, ...optimisticCreated]
            );
        });
    }, [area?.id, canEdit, orderedCues, appContext, defaultTriggerSet, buildOptimisticCreatedCues, mergeSpotsIntoContext, applyFullChainByIds, defaultRunOnes]);

    const handleAddAmbientCue = useCallback(() => {
        if (!area || !canEdit) return;
        const ambientZones = Array.isArray(area.zones?.ambientZones) ? area.zones.ambientZones : [];
        const nextIndex = ambientZones.length + 1;
        const newZone: Zone = {
            ...AreaZoneTemplate,
            id: uuidv4(),
            name: `Ambient ${nextIndex}`,
            label: `Ambient ${nextIndex}`,
            stopOtherAmbients: false,
            ...applyCueTriggerSetToSpot(defaultTriggerSet),
            soundFiles: {
                ...AreaZoneTemplate.soundFiles
            }
        };
        const entriesAfterAdd: CueListEntry[] = [...mergedCueList, {kind: "ambient", zone: newZone}];
        const zonesAfterAdd = [...ambientZones, newZone];
        const normalized = normalizeAmbientCueRules(entriesAfterAdd, zonesAfterAdd);

        appContext.updateArea(area, {
            ...area,
            zones: {
                ...area.zones,
                ambientZones: normalized
            }
        });
    }, [appContext, area, canEdit, mergedCueList, normalizeAmbientCueRules, defaultTriggerSet]);

    const handleDeleteAmbientZone = useCallback((zone: Zone) => {
        if (!area || !canEdit) return;
        const ambientZones = Array.isArray(area.zones?.ambientZones) ? area.zones.ambientZones : [];
        const remainingZones = ambientZones.filter((item) => item.id !== zone.id);
        const entriesAfterDelete = mergedCueList.filter((entry) => entry.kind !== "ambient" || entry.zone.id !== zone.id);
        const normalized = normalizeAmbientCueRules(entriesAfterDelete, remainingZones);
        appContext.updateArea(area, {
            ...area,
            zones: {
                ...area.zones,
                ambientZones: normalized
            }
        });
    }, [appContext, area, canEdit, mergedCueList, normalizeAmbientCueRules]);

    const handleSelectAmbientZone = useCallback((zone: Zone, _event: React.MouseEvent<HTMLDivElement>) => {
        setSelectedCueIds([]);
        setLastSelectedCueId(undefined);
        setSelectedAmbientZoneId(zone.id);
    }, []);

    const applyAmbientDrop = useCallback((zoneId: string, targetRowKey: string, markerPosition: "top" | "bottom") => {
        if (!area || !canEdit) {
            return;
        }
        const sourceIndex = mergedCueList.findIndex((entry) => entry.kind === "ambient" && entry.zone.id === zoneId);
        const targetIndex = mergedCueList.findIndex((entry) => getCueListEntryKey(entry) === targetRowKey);
        if (sourceIndex < 0 || targetIndex < 0) {
            return;
        }
        const insertionIndex = targetIndex + (markerPosition === "bottom" ? 1 : 0);
        const reorderedEntries = [...mergedCueList];
        const [movedEntry] = reorderedEntries.splice(sourceIndex, 1);
        const adjustedInsertionIndex = insertionIndex > sourceIndex ? insertionIndex - 1 : insertionIndex;
        reorderedEntries.splice(Math.max(0, Math.min(adjustedInsertionIndex, reorderedEntries.length)), 0, movedEntry);
        const ambientZones = Array.isArray(area.zones?.ambientZones) ? area.zones.ambientZones : [];
        const updatedAmbientZones = normalizeAmbientCueRules(reorderedEntries, ambientZones);
        appContext.updateArea(area, {
            ...area,
            zones: {
                ...area.zones,
                ambientZones: updatedAmbientZones
            }
        });
    }, [appContext, area, canEdit, getCueListEntryKey, mergedCueList, normalizeAmbientCueRules]);

    const handleMoveAmbientZone = useCallback((zone: Zone, direction: "up" | "down") => {
        const sourceIndex = mergedCueList.findIndex((entry) => entry.kind === "ambient" && entry.zone.id === zone.id);
        if (sourceIndex < 0) {
            return;
        }
        const delta = direction === "up" ? -1 : 1;
        const targetIndex = Math.max(0, Math.min(mergedCueList.length - 1, sourceIndex + delta));
        if (targetIndex === sourceIndex) {
            return;
        }
        const targetEntry = mergedCueList[targetIndex];
        if (!targetEntry) {
            return;
        }
        applyAmbientDrop(zone.id, getCueListEntryKey(targetEntry), direction === "down" ? "bottom" : "top");
    }, [applyAmbientDrop, getCueListEntryKey, mergedCueList]);

    const handleUpdateAmbientZone = useCallback((zone: Zone, updates: Partial<Zone>) => {
        if (!area || !canEdit) return;
        const updated = {...zone, ...updates};
        appContext.updateZone(zone, updated);
    }, [area, canEdit, appContext]);

    const handleUpdate = useCallback((spot: Spot, updates: Partial<Spot>) => {
        if (!canEdit) return;
        const latestSpot = appContext.getSpot(spot.id) || spot;
        appContext.updateSpot(latestSpot, {...latestSpot, ...updates});
    }, [canEdit, appContext]);

    const openSpotTriggerEditor = useCallback((spots: Spot[]) => {
        const nextIds = Array.from(new Set(spots.map((spot) => spot.id).filter(Boolean)));
        setTriggerEditorSpotIds(nextIds);
    }, []);

    const closeSpotTriggerEditor = useCallback(() => {
        setTriggerEditorSpotIds([]);
    }, []);

    const openAmbientTriggerEditor = useCallback((zone: Zone) => {
        setTriggerEditorAmbientZoneId(zone.id);
    }, []);

    const closeAmbientTriggerEditor = useCallback(() => {
        setTriggerEditorAmbientZoneId(undefined);
    }, []);

    const triggerEditorSpots = useMemo(() => {
        return triggerEditorSpotIds
            .map((spotId) => appContext.getSpot(spotId) || allSpots.find((spot) => spot.id === spotId))
            .filter((spot): spot is Spot => !!spot);
    }, [allSpots, appContext, triggerEditorSpotIds]);

    const triggerEditorSpot = triggerEditorSpots.length === 1 ? triggerEditorSpots[0] : undefined;
    const triggerEditorAmbientZone = useMemo(() => {
        if (!triggerEditorAmbientZoneId) {
            return undefined;
        }
        const ambientZones = appContext.getArea()?.zones?.ambientZones || area?.zones?.ambientZones || [];
        return ambientZones.find((zone) => zone.id === triggerEditorAmbientZoneId);
    }, [appContext, area?.zones?.ambientZones, triggerEditorAmbientZoneId]);
    const triggerEditorSpotContexts = useMemo<SpotTriggerContext[]>(() => {
        return triggerEditorSpots.map((spot) => ({
            spotId: spot.id,
            spotName: spot.name || spot.id,
            beaconTriggers: spot.beaconTriggers || [],
            wiFiTriggers: spot.wiFiTriggers || []
        }));
    }, [triggerEditorSpots]);

    const handleSpotTriggerSave = useCallback((nextTriggerSet: CueTriggerSet) => {
        if (!canEdit || !triggerEditorSpot) {
            return;
        }
        const nextSpot = {
            ...triggerEditorSpot,
            ...applyCueTriggerSetToSpot(nextTriggerSet)
        };
        appContext.updateSpot(triggerEditorSpot, nextSpot);
    }, [appContext, canEdit, triggerEditorSpot]);

    const handleMultiSpotTriggerSave = useCallback((updates: Array<{spotId: string; triggerSet: CueTriggerSet}>) => {
        if (!canEdit || updates.length === 0) {
            return;
        }
        updates.forEach((update) => {
            const targetSpot = appContext.getSpot(update.spotId) || allSpots.find((spot) => spot.id === update.spotId);
            if (!targetSpot) {
                return;
            }
            const nextSpot = {
                ...targetSpot,
                ...applyCueTriggerSetToSpot(update.triggerSet)
            };
            appContext.updateSpot(targetSpot, nextSpot);
        });
    }, [allSpots, appContext, canEdit]);

    const handleAmbientTriggerSave = useCallback((nextTriggerSet: CueTriggerSet) => {
        if (!canEdit || !triggerEditorAmbientZone) {
            return;
        }
        const nextZone = {
            ...triggerEditorAmbientZone,
            ...applyCueTriggerSetToSpot(nextTriggerSet)
        };
        appContext.updateZone(triggerEditorAmbientZone, nextZone);
    }, [appContext, canEdit, triggerEditorAmbientZone]);

    const handleDelete = useCallback((spot: Spot) => {
        if (!canEdit) return;
        appContext.deleteSelectedSpot(spot);

        // Rebuild full chain without the deleted spot
        const remaining = orderedCues.filter(s => s.id !== spot.id);
        applyFullChain(remaining);
    }, [canEdit, orderedCues, appContext, applyFullChain]);

    const handleDeleteSelected = useCallback(() => {
        if (!canEdit || selectedCueIds.length === 0) {
            return;
        }
        const selectedIdSet = new Set(selectedCueIds);
        const selectedSpots = orderedCues.filter((spot) => selectedIdSet.has(spot.id));
        if (selectedSpots.length === 0) {
            return;
        }
        selectedSpots.forEach((spot) => appContext.deleteSelectedSpot(spot));
        const remaining = orderedCues.filter((spot) => !selectedIdSet.has(spot.id));
        applyFullChain(remaining);
        setSelectedCueIds([]);
        setLastSelectedCueId(undefined);
    }, [appContext, applyFullChain, canEdit, orderedCues, selectedCueIds]);

    const rebuildChainAfterMove = useCallback((fromIndex: number, toIndex: number, prebuiltMergedList?: CueListEntry[]) => {
        if (!canEdit || fromIndex === toIndex) return;
        const areaId = area?.id;
        const previousArea = area;
        const sourceSpot = orderedCues[fromIndex];
        if (!areaId || !sourceSpot?.id) {
            return;
        }
        const clampedTo = Math.max(0, Math.min(toIndex, Math.max(0, orderedCues.length - 1)));
        if (clampedTo === fromIndex) {
            return;
        }

        const previousSpots = [...allSpots];
        const reordered = [...orderedCues];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(clampedTo, 0, moved);

        const oldFirst = orderedCues[0];
        const newFirst = reordered[0];
        const firstTriggerFields = applyCueTriggerSetToSpot(defaultTriggerSet);
        const chainUpdates = buildTriggerChain(reordered);
        const optimisticUpdatesById = new Map<string, Spot>();
        chainUpdates.forEach(({spot, updates}, index) => {
            const merged: Spot = {
                ...spot,
                ...updates
            };
            if (index === 0) {
                merged.beaconTriggers = firstTriggerFields.beaconTriggers || [];
                merged.wiFiTriggers = firstTriggerFields.wiFiTriggers || [];
            } else if (oldFirst?.id !== newFirst?.id && spot.id === oldFirst?.id) {
                merged.beaconTriggers = [];
                merged.wiFiTriggers = [];
            }
            optimisticUpdatesById.set(spot.id, merged);
        });

        let nextArea = previousArea;
        if (useAmbientRules) {
            // For >= 4.1: rebuild rules from a properly reordered merged list.
            const ambientZones = area?.zones?.ambientZones || [];
            let projectedEntries: CueListEntry[];
            if (prebuiltMergedList) {
                // Caller provided the correct merged list (e.g., spot dropped on ambient row)
                projectedEntries = prebuiltMergedList;
            } else {
                // Move the spot within the merged list to match the new spot order.
                // This correctly handles spots crossing ambient boundaries.
                // Use the original orderedCues to find the target position spot.
                const targetSpotId = orderedCues[clampedTo]?.id;
                const spotMergedFrom = mergedCueList.findIndex((e) => e.kind === "spot" && e.spot.id === sourceSpot.id);
                const spotMergedTarget = mergedCueList.findIndex((e) => e.kind === "spot" && e.spot.id === targetSpotId);
                projectedEntries = [...mergedCueList];
                if (spotMergedFrom >= 0 && spotMergedTarget >= 0 && spotMergedFrom !== spotMergedTarget) {
                    const [movedEntry] = projectedEntries.splice(spotMergedFrom, 1);
                    const adjustedTarget = spotMergedTarget > spotMergedFrom ? spotMergedTarget - 1 : spotMergedTarget;
                    const insertAt = clampedTo > fromIndex ? adjustedTarget + 1 : adjustedTarget;
                    projectedEntries.splice(insertAt, 0, movedEntry);
                }
            }
            const {updatedZones, spotManagedRules} = buildMergedCueListRules(projectedEntries, ambientZones);
            // Override trigger rules on spots with managed rules
            spotManagedRules.forEach((managedRules, spotId) => {
                const current = optimisticUpdatesById.get(spotId);
                if (!current) return;
                if (managedRules.length > 0) {
                    current.triggerRules = {
                        logic: RulesLogic.AND,
                        rules: managedRules
                    };
                } else {
                    const existingRules = current.triggerRules?.rules || [];
                    current.triggerRules = {
                        logic: current.triggerRules?.logic || RulesLogic.AND,
                        rules: existingRules.filter((r) => !isManagedCueRule(r))
                    };
                }
            });
            if (updatedZones.length > 0 && previousArea) {
                nextArea = {
                    ...previousArea,
                    zones: {
                        ...previousArea.zones,
                        ambientZones: updatedZones
                    }
                };
            }
        }

        // Batch spots and area updates together to avoid intermediate render glitch
        appContext.setSpots(previousSpots.map((spot) => optimisticUpdatesById.get(spot.id) || spot));
        if (nextArea && (nextArea !== previousArea || previousArea?.hasUnpublishedChanges !== true)) {
            appContext.setArea({
                ...nextArea,
                hasUnpublishedChanges: true
            });
        }

        // Save the optimistic spot state so we can re-apply ambient rules after server response
        appContext.trackSavePromise(moveSpotToPosition({
            variables: {
                areaId,
                spotKey: sourceSpot.key,
                toPosition: clampedTo
            }
        })).then((result) => {
            const updatedSpots = result.data?.moveSpotToPosition;
            if (Array.isArray(updatedSpots)) {
                if (useAmbientRules) {
                    // In ambient rules mode, the client manages rules and ordering.
                    // The server persists the changes but its response uses a different
                    // rule system. Don't override the correct optimistic state.
                } else {
                    appContext.setSpots(updatedSpots);
                }
            }
        }).catch((error) => {
            console.error(error);
            appContext.setSpots(previousSpots);
            if (previousArea) {
                appContext.setArea(previousArea);
            }
            appContext.showError("Unable to reorder cue");
        });
    }, [allSpots, area, appContext, canEdit, mergedCueList, moveSpotToPosition, orderedCues, defaultTriggerSet, useAmbientRules]);

    const handleMoveUp = useCallback((index: number) => {
        if (index <= 0) return;
        rebuildChainAfterMove(index, index - 1);
    }, [rebuildChainAfterMove]);

    const handleMoveDown = useCallback((index: number) => {
        if (index >= orderedCues.length - 1) return;
        rebuildChainAfterMove(index, index + 1);
    }, [rebuildChainAfterMove, orderedCues.length]);

    const handleCueRowDragStart = useCallback((event: React.DragEvent<HTMLDivElement>, spot: Spot) => {
        if (!canEdit) {
            return;
        }
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(CUE_ROW_DRAG_TYPE, spot.id);
        event.dataTransfer.setData("text/plain", spot.id);
        setSelectedCueIds([spot.id]);
        setLastSelectedCueId(spot.id);
        setSelectedAmbientZoneId(undefined);
        setDropTargetAmbientZoneId(undefined);
        setDraggingCueId(spot.id);
        setDraggingAmbientZoneId(undefined);
        setReorderTargetRowKey(undefined);
        setReorderMarkerPosition("top");
    }, [canEdit]);

    const handleAmbientRowDragStart = useCallback((event: React.DragEvent<HTMLDivElement>, zone: Zone) => {
        if (!canEdit) {
            return;
        }
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(AMBIENT_ROW_DRAG_TYPE, zone.id);
        event.dataTransfer.setData("text/plain", zone.id);
        setSelectedCueIds([]);
        setLastSelectedCueId(undefined);
        setSelectedAmbientZoneId(zone.id);
        setDraggingCueId(undefined);
        setDraggingAmbientZoneId(zone.id);
        setReorderTargetRowKey(undefined);
        setDropTargetCueId(undefined);
        setDropTargetAmbientZoneId(undefined);
        setReorderMarkerPosition("top");
    }, [canEdit]);

    const handleSelectCue = useCallback((spot: Spot, event: React.MouseEvent<HTMLDivElement>) => {
        setSelectedAmbientZoneId(undefined);
        const isCtrlMeta = event.ctrlKey || event.metaKey;
        const isShift = event.shiftKey;
        const clickedId = spot.id;
        if (isShift) {
            if (selectedCueIds.length === 0 || !lastSelectedCueId) {
                setSelectedCueIds([clickedId]);
                setLastSelectedCueId(clickedId);
                return;
            }
            const anchorIndex = orderedCues.findIndex((cue) => cue.id === lastSelectedCueId);
            const clickedIndex = orderedCues.findIndex((cue) => cue.id === clickedId);
            if (anchorIndex < 0 || clickedIndex < 0) {
                setSelectedCueIds([clickedId]);
                setLastSelectedCueId(clickedId);
                return;
            }
            const from = Math.min(anchorIndex, clickedIndex);
            const to = Math.max(anchorIndex, clickedIndex);
            const rangeIds = orderedCues.slice(from, to + 1).map((cue) => cue.id);
            setSelectedCueIds((previous) => Array.from(new Set([...previous, ...rangeIds])));
            setLastSelectedCueId(clickedId);
            return;
        }
        if (isCtrlMeta) {
            setSelectedCueIds((previous) => {
                if (previous.includes(clickedId)) {
                    const next = previous.filter((id) => id !== clickedId);
                    setLastSelectedCueId((current) => {
                        if (current !== clickedId) {
                            return current;
                        }
                        return next.length > 0 ? next[next.length - 1] : undefined;
                    });
                    return next;
                }
                setLastSelectedCueId(clickedId);
                return [...previous, clickedId];
            });
            return;
        }
        setSelectedCueIds([clickedId]);
        setLastSelectedCueId(clickedId);
    }, [lastSelectedCueId, orderedCues, selectedCueIds.length]);

    const handleCueRowDragEnd = useCallback(() => {
        setDraggingCueId(undefined);
        setDraggingAmbientZoneId(undefined);
        setReorderTargetRowKey(undefined);
        setReorderMarkerPosition("top");
    }, []);

    const handleAmbientRowDragEnd = useCallback(() => {
        setDraggingCueId(undefined);
        setDraggingAmbientZoneId(undefined);
        setReorderTargetRowKey(undefined);
        setReorderMarkerPosition("top");
    }, []);

    const [uploadingByCueId, setUploadingByCueId] = useState<Record<string, string>>({});
    const markCueUploading = useCallback((spotId: string, fileName: string) => {
        setUploadingByCueId((prev) => ({...prev, [spotId]: fileName}));
    }, []);
    const clearCueUploading = useCallback((spotId: string) => {
        setUploadingByCueId((prev) => {
            if (!(spotId in prev)) {
                return prev;
            }
            const next = {...prev};
            delete next[spotId];
            return next;
        });
    }, []);
    const [uploadingByAmbientZoneId, setUploadingByAmbientZoneId] = useState<Record<string, string>>({});
    const markAmbientUploading = useCallback((zoneId: string, fileName: string) => {
        setUploadingByAmbientZoneId((prev) => ({...prev, [zoneId]: fileName}));
    }, []);
    const clearAmbientUploading = useCallback((zoneId: string) => {
        setUploadingByAmbientZoneId((prev) => {
            if (!(zoneId in prev)) {
                return prev;
            }
            const next = {...prev};
            delete next[zoneId];
            return next;
        });
    }, []);

    const {
        assignUploadedFileToSpot,
        assignUploadedFileToAmbientZone,
        assignExistingSoundToSpot,
        assignExistingSoundToAmbientZone,
        createCuesFromDroppedFiles,
        createCueFromExistingSound
    } = useCueListSoundAssignment({
        appContext, userContext, area, canEdit, orderedCues, defaultTriggerSet,
        handleUpdate, handleUpdateAmbientZone, registerSoundFile, enqueueUploads,
        buildOptimisticCreatedCues, mergeSpotsIntoContext, applyFullChainByIds,
        markCueUploading, clearCueUploading
    });

    const handleRowDragOver = useCallback((event: React.DragEvent<HTMLDivElement>, spot: Spot) => {
        setIsListDropTarget(false);
        const draggedCueId = draggingCueId || getCueRowDragId(event.dataTransfer);
        if (draggedCueId) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "move";
            const fromIndex = orderedCues.findIndex((cue) => cue.id === draggedCueId);
            const hoveredIndex = orderedCues.findIndex((cue) => cue.id === spot.id);
            const markerPosition = computeReorderMarkerPosition(fromIndex, hoveredIndex);
            const hoveredRowKey = getSpotRowKey(spot.id);
            if (draggedCueId !== spot.id && (reorderTargetRowKey !== hoveredRowKey || reorderMarkerPosition !== markerPosition)) {
                setReorderTargetRowKey(hoveredRowKey);
                setReorderMarkerPosition(markerPosition);
            }
            return;
        }

        const draggedAmbientId = draggingAmbientZoneId || getAmbientRowDragId(event.dataTransfer);
        if (draggedAmbientId) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "move";
            const sourceIndex = mergedCueList.findIndex((entry) => entry.kind === "ambient" && entry.zone.id === draggedAmbientId);
            const hoveredIndex = mergedCueList.findIndex((entry) => entry.kind === "spot" && entry.spot.id === spot.id);
            const markerPosition = computeReorderMarkerPosition(sourceIndex, hoveredIndex);
            const hoveredRowKey = getSpotRowKey(spot.id);
            if (reorderTargetRowKey !== hoveredRowKey || reorderMarkerPosition !== markerPosition) {
                setReorderTargetRowKey(hoveredRowKey);
                setReorderMarkerPosition(markerPosition);
            }
            return;
        }

        if (hasSoundLibraryDragType(event.dataTransfer)) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "copy";
            if (dropTargetCueId !== spot.id) {
                setDropTargetCueId(spot.id);
            }
            if (dropTargetAmbientZoneId) {
                setDropTargetAmbientZoneId(undefined);
            }
            return;
        }

        if (!hasDroppedFiles(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        if (!canEdit) {
            event.dataTransfer.dropEffect = "none";
            return;
        }
        event.dataTransfer.dropEffect = "copy";
        if (dropTargetCueId !== spot.id) {
            setDropTargetCueId(spot.id);
        }
        if (dropTargetAmbientZoneId) {
            setDropTargetAmbientZoneId(undefined);
        }
    }, [canEdit, draggingAmbientZoneId, draggingCueId, dropTargetAmbientZoneId, dropTargetCueId, mergedCueList, orderedCues, reorderMarkerPosition, reorderTargetRowKey]);

    const handleRowDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>, spot: Spot) => {
        const related = event.relatedTarget as Node | null;
        if (related && event.currentTarget.contains(related)) {
            return;
        }
        const rowKey = getSpotRowKey(spot.id);
        setReorderTargetRowKey((previous) => previous === rowKey ? undefined : previous);
        setReorderMarkerPosition("top");
        setDropTargetCueId((previous) => previous === spot.id ? undefined : previous);
        setDropTargetAmbientZoneId(undefined);
    }, []);

    const handleRowDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>, spot: Spot) => {
        setIsListDropTarget(false);
        const draggedCueId = draggingCueId || getCueRowDragId(event.dataTransfer);
        if (draggedCueId) {
            event.preventDefault();
            setReorderTargetRowKey(undefined);
            setDraggingCueId(undefined);
            setDraggingAmbientZoneId(undefined);
            setDropTargetAmbientZoneId(undefined);
            setReorderMarkerPosition("top");
            if (!canEdit || draggedCueId === spot.id) {
                return;
            }
            const fromIndex = orderedCues.findIndex((cue) => cue.id === draggedCueId);
            const rawTargetIndex = orderedCues.findIndex((cue) => cue.id === spot.id);
            if (fromIndex < 0 || rawTargetIndex < 0 || fromIndex === rawTargetIndex) {
                return;
            }
            // Upward drag inserts before hovered row (top marker).
            // Downward drag inserts after hovered row (bottom marker).
            const targetIndex = rawTargetIndex;
            if (fromIndex === targetIndex) {
                return;
            }
            rebuildChainAfterMove(fromIndex, targetIndex);
            return;
        }

        const draggedAmbientId = draggingAmbientZoneId || getAmbientRowDragId(event.dataTransfer);
        if (draggedAmbientId) {
            event.preventDefault();
            setReorderTargetRowKey(undefined);
            setDraggingCueId(undefined);
            setDraggingAmbientZoneId(undefined);
            setReorderMarkerPosition("top");
            if (!canEdit) {
                return;
            }
            const targetRowKey = getSpotRowKey(spot.id);
            const markerPosition = reorderTargetRowKey === targetRowKey
                ? reorderMarkerPosition
                : "top";
            applyAmbientDrop(draggedAmbientId, targetRowKey, markerPosition);
            return;
        }

        if (hasSoundLibraryDragType(event.dataTransfer)) {
            event.preventDefault();
            if (!canEdit) {
                return;
            }
            const draggedSound = getSoundLibraryDragPayload(event.dataTransfer);
            if (!draggedSound) {
                setDropTargetCueId(undefined);
                setDropTargetAmbientZoneId(undefined);
                return;
            }
            setDropTargetCueId(undefined);
            setDropTargetAmbientZoneId(undefined);

            const latestSpot = appContext.getSpot(spot.id) || spot;
            if (hasExistingSound(latestSpot.soundFiles?.[0])) {
                const approved = await confirmSoundReplacement(confirm, `Cue "${latestSpot.name}"`, draggedSound.originalFileName, false);
                if (!approved) return;
            }
            assignExistingSoundToSpot(latestSpot, draggedSound);
            return;
        }

        if (!hasDroppedFiles(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        if (!canEdit) {
            return;
        }
        setDropTargetCueId(undefined);
        setDropTargetAmbientZoneId(undefined);

        const droppedFiles = Array.from(event.dataTransfer.files || []);
        if (droppedFiles.length === 0) {
            return;
        }
        if (droppedFiles.length > 1) {
            await createCuesFromDroppedFiles(droppedFiles);
            return;
        }
        const droppedFile = droppedFiles[0];

        const latestSpot = appContext.getSpot(spot.id) || spot;
        if (hasExistingSound(latestSpot.soundFiles?.[0])) {
            const approved = await confirmSoundReplacement(confirm, `Cue "${latestSpot.name}"`, droppedFile.name, false);
            if (!approved) return;
        }

        markCueUploading(spot.id, droppedFile.name);
        enqueueUploads(
            [droppedFile],
            async (uploaded) => {
                try {
                    await assignUploadedFileToSpot(spot, uploaded);
                } finally {
                    clearCueUploading(spot.id);
                }
            },
            () => {
                // Upload cancelled or failed — clear the placeholder; existing sound (if any) stays in place
                clearCueUploading(spot.id);
            }
        );
    }, [appContext, applyAmbientDrop, assignExistingSoundToSpot, assignUploadedFileToSpot, canEdit, clearCueUploading, confirm, createCuesFromDroppedFiles, draggingAmbientZoneId, draggingCueId, enqueueUploads, markCueUploading, orderedCues, rebuildChainAfterMove, reorderMarkerPosition, reorderTargetRowKey]);

    const handleAmbientRowDragOver = useCallback((event: React.DragEvent<HTMLDivElement>, zone: Zone) => {
        setIsListDropTarget(false);
        const draggedCueId = draggingCueId || getCueRowDragId(event.dataTransfer);
        if (draggedCueId) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "move";
            const sourceIndex = mergedCueList.findIndex((entry) => entry.kind === "spot" && entry.spot.id === draggedCueId);
            const hoveredIndex = mergedCueList.findIndex((entry) => entry.kind === "ambient" && entry.zone.id === zone.id);
            const markerPosition = computeReorderMarkerPosition(sourceIndex, hoveredIndex);
            const hoveredRowKey = getAmbientRowKey(zone.id);
            if (reorderTargetRowKey !== hoveredRowKey || reorderMarkerPosition !== markerPosition) {
                setReorderTargetRowKey(hoveredRowKey);
                setReorderMarkerPosition(markerPosition);
            }
            return;
        }

        const draggedAmbientId = draggingAmbientZoneId || getAmbientRowDragId(event.dataTransfer);
        if (draggedAmbientId) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "move";
            const sourceIndex = mergedCueList.findIndex((entry) => entry.kind === "ambient" && entry.zone.id === draggedAmbientId);
            const hoveredIndex = mergedCueList.findIndex((entry) => entry.kind === "ambient" && entry.zone.id === zone.id);
            const markerPosition = computeReorderMarkerPosition(sourceIndex, hoveredIndex);
            const hoveredRowKey = getAmbientRowKey(zone.id);
            if (draggedAmbientId !== zone.id && (reorderTargetRowKey !== hoveredRowKey || reorderMarkerPosition !== markerPosition)) {
                setReorderTargetRowKey(hoveredRowKey);
                setReorderMarkerPosition(markerPosition);
            }
            return;
        }

        if (hasSoundLibraryDragType(event.dataTransfer)) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "copy";
            if (dropTargetAmbientZoneId !== zone.id) {
                setDropTargetAmbientZoneId(zone.id);
            }
            if (dropTargetCueId) {
                setDropTargetCueId(undefined);
            }
            return;
        }

        if (!hasDroppedFiles(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        if (!canEdit) {
            event.dataTransfer.dropEffect = "none";
            return;
        }
        event.dataTransfer.dropEffect = "copy";
        if (dropTargetAmbientZoneId !== zone.id) {
            setDropTargetAmbientZoneId(zone.id);
        }
        if (dropTargetCueId) {
            setDropTargetCueId(undefined);
        }
    }, [canEdit, draggingAmbientZoneId, draggingCueId, dropTargetAmbientZoneId, dropTargetCueId, mergedCueList, reorderMarkerPosition, reorderTargetRowKey]);

    const handleAmbientRowDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>, zone: Zone) => {
        const related = event.relatedTarget as Node | null;
        if (related && event.currentTarget.contains(related)) {
            return;
        }
        const rowKey = getAmbientRowKey(zone.id);
        setReorderTargetRowKey((previous) => previous === rowKey ? undefined : previous);
        setReorderMarkerPosition("top");
        setDropTargetAmbientZoneId((previous) => previous === zone.id ? undefined : previous);
    }, []);

    const handleAmbientRowDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>, zone: Zone) => {
        setIsListDropTarget(false);
        const draggedCueId = draggingCueId || getCueRowDragId(event.dataTransfer);
        if (draggedCueId) {
            event.preventDefault();
            setReorderTargetRowKey(undefined);
            setDraggingCueId(undefined);
            setDraggingAmbientZoneId(undefined);
            setDropTargetAmbientZoneId(undefined);
            setReorderMarkerPosition("top");
            if (!canEdit) {
                return;
            }
            const fromIndex = orderedCues.findIndex((cue) => cue.id === draggedCueId);
            if (fromIndex < 0) {
                return;
            }
            // Reorder spot in the merged list relative to the ambient
            const spotMergedIndex = mergedCueList.findIndex((entry) => entry.kind === "spot" && entry.spot.id === draggedCueId);
            const ambientMergedIndex = mergedCueList.findIndex((entry) => entry.kind === "ambient" && entry.zone.id === zone.id);
            const targetRowKey = getAmbientRowKey(zone.id);
            const markerPosition = reorderTargetRowKey === targetRowKey ? reorderMarkerPosition : "top";
            if (spotMergedIndex < 0 || ambientMergedIndex < 0) {
                return;
            }
            // Build reordered merged list with the spot moved
            const reorderedEntries = [...mergedCueList];
            const [movedEntry] = reorderedEntries.splice(spotMergedIndex, 1);
            const insertAt = markerPosition === "top" ? ambientMergedIndex : ambientMergedIndex + 1;
            const adjustedInsertAt = insertAt > spotMergedIndex ? insertAt - 1 : insertAt;
            reorderedEntries.splice(Math.max(0, Math.min(adjustedInsertAt, reorderedEntries.length)), 0, movedEntry);
            // Derive new spot order from the reordered merged list
            const newSpotOrder = reorderedEntries
                .filter((e): e is {kind: "spot"; spot: Spot; index: number} => e.kind === "spot")
                .map((e) => e.spot);
            const newTargetIndex = newSpotOrder.findIndex((s) => s.id === draggedCueId);
            if (newTargetIndex >= 0 && newTargetIndex !== fromIndex) {
                rebuildChainAfterMove(fromIndex, newTargetIndex, reorderedEntries);
            } else if (useAmbientRules) {
                // Spot didn't change position among spots but crossed an ambient
                // boundary. Re-normalize rules based on the new merged order.
                const ambientZones = area?.zones?.ambientZones || [];
                if (ambientZones.length > 0) {
                    const updatedAmbientZones = normalizeAmbientCueRules(reorderedEntries, ambientZones);
                    const currentArea = appContext.getArea();
                    if (currentArea) {
                        appContext.updateArea(currentArea, {
                            ...currentArea,
                            zones: {
                                ...currentArea.zones,
                                ambientZones: updatedAmbientZones
                            }
                        });
                    }
                }
            }
            return;
        }

        const draggedAmbientId = draggingAmbientZoneId || getAmbientRowDragId(event.dataTransfer);
        if (draggedAmbientId) {
            event.preventDefault();
            setReorderTargetRowKey(undefined);
            setDraggingCueId(undefined);
            setDraggingAmbientZoneId(undefined);
            setDropTargetAmbientZoneId(undefined);
            setReorderMarkerPosition("top");
            if (!canEdit || draggedAmbientId === zone.id) {
                return;
            }
            const targetRowKey = getAmbientRowKey(zone.id);
            const markerPosition = reorderTargetRowKey === targetRowKey
                ? reorderMarkerPosition
                : "top";
            applyAmbientDrop(draggedAmbientId, targetRowKey, markerPosition);
            return;
        }

        if (hasSoundLibraryDragType(event.dataTransfer)) {
            event.preventDefault();
            if (!canEdit) {
                return;
            }
            const draggedSound = getSoundLibraryDragPayload(event.dataTransfer);
            if (!draggedSound) {
                setDropTargetAmbientZoneId(undefined);
                return;
            }
            setDropTargetAmbientZoneId(undefined);
            const latestArea = appContext.getArea() || area;
            const latestZone = latestArea?.zones?.ambientZones?.find((item) => item.id === zone.id) || zone;
            if (hasExistingSound(latestZone.soundFiles?.ambient)) {
                const approved = await confirmSoundReplacement(confirm, `Ambient "${latestZone.label || latestZone.name || latestZone.id}"`, draggedSound.originalFileName, true);
                if (!approved) return;
            }
            assignExistingSoundToAmbientZone(latestZone, draggedSound);
            return;
        }

        if (!hasDroppedFiles(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        if (!canEdit) {
            return;
        }
        setDropTargetAmbientZoneId(undefined);
        const droppedFiles = Array.from(event.dataTransfer.files || []);
        if (droppedFiles.length === 0) {
            return;
        }
        if (droppedFiles.length > 1) {
            await createCuesFromDroppedFiles(droppedFiles);
            return;
        }
        const droppedFile = droppedFiles[0];
        const latestArea = appContext.getArea() || area;
        const latestZone = latestArea?.zones?.ambientZones?.find((item) => item.id === zone.id) || zone;
        if (hasExistingSound(latestZone.soundFiles?.ambient)) {
            const approved = await confirmSoundReplacement(confirm, `Ambient "${latestZone.label || latestZone.name || latestZone.id}"`, droppedFile.name, true);
            if (!approved) return;
        }
        markAmbientUploading(latestZone.id, droppedFile.name);
        enqueueUploads(
            [droppedFile],
            async (uploaded) => {
                try {
                    await assignUploadedFileToAmbientZone(latestZone, uploaded);
                } finally {
                    clearAmbientUploading(latestZone.id);
                }
            },
            () => {
                clearAmbientUploading(latestZone.id);
            }
        );
    }, [appContext, applyAmbientDrop, area, assignExistingSoundToAmbientZone, assignUploadedFileToAmbientZone, canEdit, clearAmbientUploading, confirm, createCuesFromDroppedFiles, draggingAmbientZoneId, draggingCueId, enqueueUploads, markAmbientUploading, mergedCueList, normalizeAmbientCueRules, orderedCues, rebuildChainAfterMove, reorderMarkerPosition, reorderTargetRowKey, useAmbientRules]);

    const handleListDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (draggingCueId || getCueRowDragId(event.dataTransfer) || draggingAmbientZoneId || getAmbientRowDragId(event.dataTransfer)) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "move";
            return;
        }
        if (hasSoundLibraryDragType(event.dataTransfer)) {
            event.preventDefault();
            if (!canEdit) {
                event.dataTransfer.dropEffect = "none";
                return;
            }
            event.dataTransfer.dropEffect = "copy";
            setIsListDropTarget(true);
            return;
        }
        if (!hasDroppedFiles(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        if (!canEdit) {
            event.dataTransfer.dropEffect = "none";
            return;
        }
        event.dataTransfer.dropEffect = "copy";
        setIsListDropTarget(true);
    }, [canEdit, draggingAmbientZoneId, draggingCueId]);

    const handleListDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        const related = event.relatedTarget as Node | null;
        if (related && event.currentTarget.contains(related)) {
            return;
        }
        setIsListDropTarget(false);
        setDropTargetCueId(undefined);
        setDropTargetAmbientZoneId(undefined);
    }, []);

    const handleListDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        const droppedCueId = draggingCueId || getCueRowDragId(event.dataTransfer);
        const droppedAmbientZoneId = draggingAmbientZoneId || getAmbientRowDragId(event.dataTransfer);
        if (droppedCueId || droppedAmbientZoneId) {
            event.preventDefault();
            setIsListDropTarget(false);
            setDropTargetCueId(undefined);
            setDropTargetAmbientZoneId(undefined);
            setReorderTargetRowKey(undefined);
            setDraggingCueId(undefined);
            setDraggingAmbientZoneId(undefined);
            setReorderMarkerPosition("top");
            if (!canEdit) {
                return;
            }
            if (droppedCueId) {
                const fromIndex = orderedCues.findIndex((cue) => cue.id === droppedCueId);
                const toIndex = Math.max(0, orderedCues.length - 1);
                if (fromIndex >= 0 && fromIndex !== toIndex) {
                    rebuildChainAfterMove(fromIndex, toIndex);
                }
                return;
            }
            if (droppedAmbientZoneId && mergedCueList.length > 0) {
                const lastEntry = mergedCueList[mergedCueList.length - 1];
                const lastEntryKey = getCueListEntryKey(lastEntry);
                applyAmbientDrop(droppedAmbientZoneId, lastEntryKey, "bottom");
            }
            return;
        }
        if (hasSoundLibraryDragType(event.dataTransfer)) {
            event.preventDefault();
            setIsListDropTarget(false);
            setDropTargetCueId(undefined);
            setDropTargetAmbientZoneId(undefined);
            if (!canEdit || !area?.id) {
                return;
            }
            const payload = getSoundLibraryDragPayload(event.dataTransfer);
            if (!payload) {
                return;
            }
            await createCueFromExistingSound(payload);
            return;
        }
        if (!hasDroppedFiles(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        setIsListDropTarget(false);
        setDropTargetCueId(undefined);
        setDropTargetAmbientZoneId(undefined);

        if (!canEdit || !area?.id) {
            return;
        }

        const droppedFiles = Array.from(event.dataTransfer.files || []);
        if (droppedFiles.length === 0) {
            return;
        }
        await createCuesFromDroppedFiles(droppedFiles);
    }, [
        area?.id,
        canEdit,
        createCueFromExistingSound,
        createCuesFromDroppedFiles,
        draggingAmbientZoneId,
        draggingCueId,
        orderedCues,
        rebuildChainAfterMove,
        mergedCueList,
        getCueListEntryKey,
        applyAmbientDrop
    ]);

    const getTransitionLabel = useCallback((previousSpot: Spot | undefined, nextSpot: Spot) => {
        if (!previousSpot) {
            return "start";
        }
        const nextDelay = nextSpot.soundFiles?.[0]?.delay ?? 0;
        const prevFadeOut = previousSpot.soundFiles?.[0]?.fadeOut ?? 0;
        const nextFadeIn = nextSpot.soundFiles?.[0]?.fadeIn ?? 0;
        const hasCrossfade = prevFadeOut > 0 && nextFadeIn > 0 && nextDelay === 0;
        if (hasCrossfade) {
            return `crossfade ${formatMillisecondsAsTimeCode(prevFadeOut)} / ${formatMillisecondsAsTimeCode(nextFadeIn)}`;
        }
        if (nextDelay > 0) {
            return `${formatMillisecondsAsTimeCode(nextDelay)} gap`;
        }
        return "immediate";
    }, []);

    const selectedCues = orderedCues.filter((spot) => selectedCueIds.includes(spot.id));
    const isMultiSpotSelection = selectedCues.length > 1;
    const firstSelectedSpotId = isMultiSpotSelection
        ? orderedCues.find((spot) => selectedCueIds.includes(spot.id))?.id
        : undefined;
    const selectedCue = selectedCues.length === 1 ? selectedCues[0] : undefined;
    const selectedCueNumber = selectedCue ? orderedCues.findIndex((cue) => cue.id === selectedCue.id) + 1 : undefined;
    const ambientZones: Zone[] = area?.zones?.ambientZones || [];
    const selectedAmbientZone = selectedAmbientZoneId ? ambientZones.find(z => z.id === selectedAmbientZoneId) : undefined;
    const renderCueEntry = (entry: CueListEntry) => {
        const entryKey = getCueListEntryKey(entry);
        const entryIndex = mergedEntryIndexByKey.get(entryKey) ?? 0;
        if (entry.kind === "ambient") {
            return (
                <CueListAmbientRow
                    key={`ambient-${entry.zone.id}`}
                    zone={entry.zone}
                    canEdit={canEdit}
                    isSelected={selectedAmbientZoneId === entry.zone.id}
                    isDropTarget={dropTargetAmbientZoneId === entry.zone.id}
                    isReorderTarget={reorderTargetRowKey === getAmbientRowKey(entry.zone.id)}
                    reorderMarkerPosition={reorderMarkerPosition}
                    isDragging={draggingAmbientZoneId === entry.zone.id}
                    canMoveUp={entryIndex > 0}
                    canMoveDown={entryIndex < mergedCueList.length - 1}
                    onSelect={handleSelectAmbientZone}
                    onDelete={handleDeleteAmbientZone}
                    onMoveUp={(zone) => handleMoveAmbientZone(zone, "up")}
                    onMoveDown={(zone) => handleMoveAmbientZone(zone, "down")}
                    onRowDragStart={handleAmbientRowDragStart}
                    onRowDragEnd={handleAmbientRowDragEnd}
                    onRowDragOver={handleAmbientRowDragOver}
                    onRowDragLeave={handleAmbientRowDragLeave}
                    onRowDrop={handleAmbientRowDrop}
                    showActions={!isMultiSpotSelection}
                    uploadingFileName={uploadingByAmbientZoneId[entry.zone.id]}
                />
            );
        }
        const {spot, index} = entry;
        const isSpotSelected = selectedCueIds.includes(spot.id);
        const isPrimaryControlRow = !isMultiSpotSelection || spot.id === firstSelectedSpotId;
        const useBulkDelete = isMultiSpotSelection && spot.id === firstSelectedSpotId;
        const translationState = getSpotTranslationState(spot) || "notTranslated";
        return (
            <CueListRow
                key={spot.id}
                spot={spot}
                index={index}
                transitionLabel={getTransitionLabel(index > 0 ? orderedCues[index - 1] : undefined, spot)}
                canEdit={canEdit}
                isSelected={isSpotSelected}
                isDropTarget={spot.id === dropTargetCueId}
                isReorderTarget={reorderTargetRowKey === getSpotRowKey(spot.id)}
                reorderMarkerPosition={reorderMarkerPosition}
                isDragging={spot.id === draggingCueId}
                isReordering={isReordering}
                onSelect={handleSelectCue}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                onRowDragStart={handleCueRowDragStart}
                onRowDragEnd={handleCueRowDragEnd}
                onRowDragOver={handleRowDragOver}
                onRowDragLeave={handleRowDragLeave}
                onRowDrop={handleRowDrop}
                isFirst={index === 0}
                isLast={index === orderedCues.length - 1}
                showMoveActions={!isMultiSpotSelection}
                showDeleteAction={isPrimaryControlRow}
                deleteTooltipLabel={useBulkDelete ? "Delete selected cues" : "Delete cue"}
                onDelete={useBulkDelete ? (_spot: Spot) => handleDeleteSelected() : handleDelete}
                showTranslationFlag={translatableLanguageCodes.length > 0}
                translationStatusColor={getLocalizedStatusColor(translationState, isSpotSelected)}
                translationStatusLabel={getLocalizedStatusLabel(translationState)}
                onOpenTranslations={(clickedSpot) => openTranslationDialog({
                    areaId: area?.id,
                    clientId: clickedSpot.key,
                    defaultLanguage: area?.languages?.default,
                    availableLanguages: areaLanguages,
                    publishedAt: appContext.getDisplayedVersion()?.publishedAt || 0,
                    canEdit: canEdit,
                    title: `Translations: ${clickedSpot.name || "Cue"}`,
                    description: "Translate cue name, summary, and description. If a translation is left empty, it falls back to the space default language and then the main spot field value.",
                    fields: buildSpotTranslationFields(clickedSpot),
                    onTranslationSaved: mergeSpotTranslation
                })}
                showAudioIndicator={!!spot.soundFiles?.length}
                audioIndicatorColor={isSpotSelected ? "#d97706" : undefined}
                uploadingFileName={uploadingByCueId[spot.id]}
            />
        );
    };

    return (
        <Box
            height="100%"
            minH={0}
            display="flex"
            flexDirection="column"
            position={"relative"}
            bg={isListDropTarget ? "teal.50" : "gray.50"}
            overflow={"hidden"}
            onDragOver={handleListDragOver}
            onDragLeave={handleListDragLeave}
            onDrop={handleListDrop}
            boxShadow={isListDropTarget ? "inset 0 0 0 1px var(--chakra-colors-teal-300)" : undefined}
        >
            <Flex
                px={6}
                py={4}
                borderBottomWidth="1px"
                borderColor="gray.200"
                bg="white"
                align="center"
                justify="space-between"
                flexShrink={0}
            >
                <Box>
                    <Heading size="sm" color="gray.800">Cue List</Heading>
                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                        {orderedCues.length} cue{orderedCues.length !== 1 ? "s" : ""}
                    </Text>
                </Box>
                <Flex gap={2}>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDefaultTriggerDialogOpen(true)}
                        isDisabled={!canEdit}
                    >
                        Default triggers
                    </Button>
                    <Button
                        leftIcon={<FiPlus/>}
                        size="sm"
                        colorScheme="teal"
                        onClick={handleAddCue}
                        isDisabled={!canEdit}
                    >
                        Add audio cue
                    </Button>
                    <Button
                        leftIcon={<FiPlus/>}
                        size="sm"
                        colorScheme="purple"
                        variant="outline"
                        onClick={handleAddAmbientCue}
                        isDisabled={!canEdit}
                    >
                        Add ambient cue
                    </Button>
                    <Tooltip label={isSoundDrawerOpen ? "Hide sound files" : "Show sound files"}>
                        <Button
                            aria-label={isSoundDrawerOpen ? "Hide sound file panel" : "Show sound file panel"}
                            onClick={onToggleSoundDrawer}
                            size={"sm"}
                            variant={"outline"}
                            borderColor={"gray.300"}
                            bg={"white"}
                            _hover={{bg: "gray.50", borderColor: "gray.400"}}
                            minWidth={"auto"}
                            paddingX={1}
                            title={isSoundDrawerOpen ? "Hide sound files" : "Show sound files"}
                        >
                            <HStack spacing={0.5}>
                                {isSoundDrawerOpen ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                                <BsSoundwave/>
                            </HStack>
                        </Button>
                    </Tooltip>
                </Flex>
            </Flex>

            <Box flex={1} minH={0} display={"flex"} flexDirection={"row"} overflow={"hidden"} position={"relative"}>
                <Box
                    width={"100%"}
                    minW={0}
                    display={"flex"}
                    flexDirection={"column"}
                    overflow={"hidden"}
                >
                    <Grid
                        gridTemplateColumns={CUE_LIST_GRID_TEMPLATE}
                        px={3}
                        py={2}
                        bg={"gray.100"}
                        borderBottomWidth={"1px"}
                        borderColor={"gray.200"}
                        fontSize={"11px"}
                        fontWeight={"bold"}
                        textTransform={"uppercase"}
                        letterSpacing={"0.05em"}
                        color={"gray.600"}
                        flexShrink={0}
                    >
                        <Text>#</Text>
                        <Text>Name</Text>
                        <Text>Sound</Text>
                        <Text>Delay</Text>
                        <Text>Fade in</Text>
                        <Text>Fade out</Text>
                        <Text>Transition</Text>
                        <Text textAlign={"center"} whiteSpace={"nowrap"}>Translation</Text>
                        <Text textAlign={"right"}>Actions</Text>
                    </Grid>

                    <Grid
                        ref={splitContainerRef}
                        flex={1}
                        minH={0}
                        gridTemplateRows={`1fr ${splitterDividerPx}px ${inspectorHeightPx}px`}
                    >
                        <Box
                            minH={0}
                            overflowY="auto"
                            userSelect={"none"}
                            sx={{WebkitUserSelect: "none"}}
                        >
                            {mergedCueList.length === 0 ? (
                                <Flex
                                    direction="column"
                                    align="center"
                                    justify="center"
                                    height="200px"
                                    color="gray.400"
                                >
                                    <Text fontSize="sm" mb={3}>No cues yet</Text>
                                    {canEdit && (
                                        <>
                                            <Button
                                            leftIcon={<FiPlus/>}
                                            size="sm"
                                            variant="outline"
                                            onClick={handleAddCue}
                                        >
                                            Add first audio cue
                                        </Button>
                                        <Text fontSize="sm" mt={3}>Or drag and drop audio files here</Text>
                                        </>
                                    )}
                                </Flex>
                            ) : (
                                <>
                                    {visualCueSections.map((entries, sectionIndex) => (
                                        <Box key={`cue-group-${sectionIndex}`}>
                                            {sectionIndex > 0 ? (
                                                <Grid
                                                    gridTemplateColumns={CUE_LIST_GRID_TEMPLATE}
                                                    alignItems={"center"}
                                                    px={3}
                                                    py={2}
                                                    borderTopWidth={"1px"}
                                                    borderBottomWidth={"1px"}
                                                    borderColor={"gray.100"}
                                                    bg={"white"}
                                                    pointerEvents={"none"}
                                                    userSelect={"none"}
                                                >
                                                    <Text fontSize={"sm"} visibility={"hidden"} userSelect={"none"}>
                                                        000
                                                    </Text>
                                                </Grid>
                                            ) : null}
                                            {entries.map((entry) => {
                                                return renderCueEntry(entry);
                                            })}
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Box>
                        <Box
                            height={`${splitterDividerPx}px`}
                            backgroundColor={"#e2e8f0"}
                            cursor={"row-resize"}
                            onMouseDown={() => {
                                setIsSplitterDragging(true);
                                if (originalSplitterUserSelect === undefined) {
                                    setOriginalSplitterUserSelect(document.body.style.userSelect);
                                }
                                document.body.style.userSelect = "none";
                            }}
                        />
                        <Box overflowY={"auto"} minHeight={0}>
                            <CueListInspector
                                spots={selectedCues}
                                cueNumber={selectedCueNumber}
                                ambientZone={selectedAmbientZone}
                                canEdit={canEdit}
                                existingSoundOptions={existingSoundOptions}
                                isLibraryLoading={isLibraryLoading}
                                onRequestLibrary={onRequestLibrary}
                                onUpdate={handleUpdate}
                                onUpdateZone={handleUpdateAmbientZone}
                                onEditSpotTriggers={openSpotTriggerEditor}
                                onEditAmbientTriggers={openAmbientTriggerEditor}
                            />
                        </Box>
                    </Grid>
                </Box>
            </Box>
            {isListDropTarget ? (
                <Box
                    position={"absolute"}
                    inset={"12px"}
                    border={"2px dashed"}
                    borderColor={"teal.400"}
                    borderRadius={"12px"}
                    bg={"rgba(56, 178, 172, 0.08)"}
                    pointerEvents={"none"}
                    zIndex={10}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                >
                    <Text
                        fontSize={"lg"}
                        fontWeight={"semibold"}
                        color={"teal.700"}
                        bg={"white"}
                        px={4}
                        py={2}
                        borderRadius={"md"}
                        boxShadow={"sm"}
                    >
                        Drop files or library sounds to create cues
                    </Text>
                </Box>
            ) : null}
            <CueTriggerEditorDialog
                isOpen={isDefaultTriggerDialogOpen}
                onClose={() => setDefaultTriggerDialogOpen(false)}
                onSave={handleDefaultTriggerSave}
                beaconTriggers={defaultTriggerSet.beaconTriggers}
                wiFiTriggers={defaultTriggerSet.wiFiTriggers}
                title={"Default triggers"}
                canEdit={canEdit}
                allowBeaconTriggers={allowBeaconTriggers}
            />
            <CueTriggerEditorDialog
                isOpen={!!triggerEditorSpot}
                onClose={closeSpotTriggerEditor}
                onSave={handleSpotTriggerSave}
                beaconTriggers={triggerEditorSpot?.beaconTriggers || []}
                wiFiTriggers={triggerEditorSpot?.wiFiTriggers || []}
                title={triggerEditorSpot ? `Spot triggers - ${triggerEditorSpot.name}` : "Spot triggers"}
                canEdit={canEdit}
                allowBeaconTriggers={allowBeaconTriggers}
            />
            <CueMultiSpotTriggerEditorDialog
                isOpen={triggerEditorSpotContexts.length > 1}
                onClose={closeSpotTriggerEditor}
                onSave={handleMultiSpotTriggerSave}
                spotContexts={triggerEditorSpotContexts}
                canEdit={canEdit}
                allowBeaconTriggers={allowBeaconTriggers}
            />
            <CueTriggerEditorDialog
                isOpen={!!triggerEditorAmbientZone}
                onClose={closeAmbientTriggerEditor}
                onSave={handleAmbientTriggerSave}
                beaconTriggers={triggerEditorAmbientZone?.beaconTriggers || []}
                wiFiTriggers={triggerEditorAmbientZone?.wiFiTriggers || []}
                title={triggerEditorAmbientZone
                    ? `Ambient triggers - ${triggerEditorAmbientZone.label || triggerEditorAmbientZone.name || triggerEditorAmbientZone.id}`
                    : "Ambient triggers"}
                canEdit={canEdit}
                allowBeaconTriggers={allowBeaconTriggers}
            />
        </Box>
    );
};

export default CueListPanel;
